import { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { getAdminUsers, suspendUser } from '../../lib/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Users, Ban, ArrowRight, Loader2 } from 'lucide-react';

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('');
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  const loadUsers = async (cursor = null, role = roleFilter) => {
    try {
      const params = { limit: 20, cursor };
      if (role) params.role = role;
      
      const response = await getAdminUsers(params);
      
      if (cursor) {
        setUsers(prev => [...prev, ...response.data.items]);
      } else {
        setUsers(response.data.items || []);
      }
      
      setNextCursor(response.data.next_cursor);
      setHasMore(response.data.has_more);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleRoleChange = (role) => {
    setRoleFilter(role);
    setLoading(true);
    loadUsers(null, role);
  };

  const handleSuspend = async (userId) => {
    if (!window.confirm('Are you sure you want to suspend this user?')) return;
    
    setActionLoading(userId);
    try {
      await suspendUser(userId);
      setUsers(prev => 
        prev.map(u => u.id === userId ? { ...u, suspended: true } : u)
      );
      toast.success('User suspended');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to suspend user');
    } finally {
      setActionLoading(null);
    }
  };

  const getRoleBadge = (role) => {
    const variants = {
      guest: 'bg-blue-100 text-blue-700',
      host: 'bg-green-100 text-green-700',
      admin: 'bg-purple-100 text-purple-700'
    };
    return variants[role] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="space-y-6" data-testid="admin-users">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Users</h1>
          <p className="text-slate-500 mt-1">Manage platform users</p>
        </div>
        <Select value={roleFilter} onValueChange={handleRoleChange}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All roles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            <SelectItem value="guest">Guests</SelectItem>
            <SelectItem value="host">Hosts</SelectItem>
            <SelectItem value="admin">Admins</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : users.length > 0 ? (
        <>
          <div className="space-y-4">
            {users.map((user) => (
              <Card key={user.id}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center font-medium text-lg">
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-slate-900">{user.name}</h3>
                          <Badge className={getRoleBadge(user.role)}>
                            {user.role}
                          </Badge>
                          {user.suspended && (
                            <Badge className="bg-red-100 text-red-700">Suspended</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">{user.email}</p>
                        <p className="text-xs text-slate-400">
                          Joined {format(new Date(user.created_at), 'MMM d, yyyy')}
                        </p>
                      </div>
                    </div>
                    
                    {user.role !== 'admin' && !user.suspended && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSuspend(user.id)}
                        disabled={actionLoading === user.id}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        {actionLoading === user.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Ban className="w-4 h-4 mr-1" />
                            Suspend
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {hasMore && (
            <div className="text-center">
              <Button 
                variant="outline"
                onClick={() => loadUsers(nextCursor)}
              >
                Load More
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
              No users found
            </h3>
            <p className="text-slate-500">
              {roleFilter ? `No ${roleFilter}s found.` : 'No users registered yet.'}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
